import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import Tabs from '~/modules/addListing/pages/ListingDescription/Tabs';
import DescriptionForm from '~/modules/addListing/pages/ListingDescription/DescriptionForm';
import { Button } from '@aqarmap/aqarmap-ui';
import {
  useAddListingDetailsMutation,
  useGenerateListingDescriptionMutation,
  useGetListingDetailsQuery,
  useGetUserQuery,
} from '~/redux/api/userApiSlice';
import { FormProvider, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  listingDescriptionARSchema,
  listingDescriptionENSchema,
} from '~/modules/addListing/utils/schema';
import {
  AddListingDescriptionType,
  ListingDetailsType,
} from '~/modules/addListing/types';
import { Languages } from '~/constants/enums';
import DescriptionHelpers from '~/modules/addListing/pages/ListingDescription/DescriptionHelpers';
import Loader from '~/components/Loader';
import { saveListingData } from '~addListing/utils/LocalStorage';
import { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { useFullRoutes } from '~/hooks';
import { getLocalizedListingPrefill } from '~addListing/utils/getLocalizedListingPrefill';

type StatusArray = Array<{
  [key: string]: {
    isSuccess: boolean;
    isLoading: boolean;
    isFetching: boolean;
  };
}>;

const ListingDescription = () => {
  const { id, locale } = useParams();
  const { ADD_LISTING_URL } = useFullRoutes();
  const pathName = useLocation().pathname;
  const { t } = useTranslation('app');
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<number>(0);
  const [generatedValuesByAI, setGeneratedValuesByAI] = useState<Record<
    string,
    string
  > | null>(null);
  const [keysGeneratedByAI, setKeysGeneratedByAI] = useState<StatusArray>([]);
  const [generateListingDescription] = useGenerateListingDescriptionMutation();
  const {
    data,
    isSuccess: isListingDetailsSuccess,
    isFetching: isFetchingListingDetails,
    refetch,
  } = useGetListingDetailsQuery(id);
  const [addListingDetails, { isLoading: isLoadingAddListingDetails }] =
    useAddListingDetailsMutation();
  const { data: userData } = useGetUserQuery();
  const listingDetails: ListingDetailsType = data?.listing;
  const methods = useForm<AddListingDescriptionType>({
    resolver: zodResolver(
      locale === Languages.ARABIC
        ? listingDescriptionARSchema
        : listingDescriptionENSchema,
    ),
  });

  const onSubmit = async (data: AddListingDescriptionType) => {
    const loadingKeys = keysGeneratedByAI.flatMap((item) => {
      return Object.entries(item)
        .filter(([, value]) => value.isLoading)
        .map(([key]) => key);
    });

    loadingKeys.map((key) => {
      data[key as keyof AddListingDescriptionType] =
        generatedValuesByAI?.[key.replace('-', '_')] || '';
    });

    if (locale === Languages.ENGLISH) {
      data['title-ar'] = data['title-ar']
        ? data['title-ar']
        : generatedValuesByAI
          ? generatedValuesByAI['title_ar']
          : '';
      data['description-ar'] = data['description-ar']
        ? data['description-ar']
        : generatedValuesByAI
          ? generatedValuesByAI['description_ar']
          : '';
      data['address-ar'] = data['address-ar'] || '';
    }
    if (locale === Languages.ARABIC) {
      data['title-en'] = data['title-en']
        ? data['title-en']
        : generatedValuesByAI
          ? generatedValuesByAI['title_en']
          : '';
      data['description-en'] = data['description-en']
        ? data['description-en']
        : generatedValuesByAI
          ? generatedValuesByAI['description_en']
          : '';
      data['address-en'] = data['address-en'] || '';
    }

    const addListingDetailsData = saveListingData(`${id}`, data);
    const res = await addListingDetails({
      id,
      body: addListingDetailsData.data,
    });
    if ('data' in res && id) navigate(ADD_LISTING_URL.PHOTOS(id));
    if ('error' in res) {
      const error = res.error as FetchBaseQueryError;
      alert('error message' + (error.data as any)?.message);
    }
  };
  const handleSetGeneratedValue = (key: any, value: any) => {
    setKeysGeneratedByAI((prev) => {
      const index = prev.findIndex((item) => key in item);
      if (index !== -1) {
        const updated = [...prev];
        updated[index] = {
          [key]: value,
        };
        return updated;
      } else return [...prev, { [key]: value }];
    });
  };
  const setGeneratedValue = (
    key: any,
    value: string,
    inputRef: React.RefObject<HTMLTextAreaElement>,
  ) => {
    const delay = 50;
    for (let i = 0; i < value.length; i++) {
      setTimeout(() => {
        methods.setValue(key, value.slice(0, i + 1));
        if (inputRef.current) {
          inputRef.current.scrollTop = inputRef.current.scrollHeight;
        }
      }, delay * i);
    }

    setTimeout(() => {
      handleSetGeneratedValue(key, {
        isLoading: false,
        isSuccess: true,
        isFetching: false,
      });
    }, value.length * delay);
  };
  const handleGenerateValueByAI = async (
    key: any,
    inputRef: React.RefObject<HTMLTextAreaElement>,
  ) => {
    handleSetGeneratedValue(key, {
      isLoading: true,
      isSuccess: false,
      isFetching: true,
    });
    if (generatedValuesByAI) {
      handleSetGeneratedValue(key, {
        isLoading: true,
        isSuccess: false,
        isFetching: false,
      });
      return setGeneratedValue(
        key,
        generatedValuesByAI[key.replace('-', '_')],
        inputRef,
      );
    }
    const res = await generateListingDescription(id);
    handleSetGeneratedValue(key, {
      isLoading: true,
      isSuccess: false,
      isFetching: false,
    });

    if ('data' in res) {
      setGeneratedValuesByAI(res.data);
      setGeneratedValue(key, res.data[key.replace('-', '_')], inputRef);
    }
  };

  useEffect(() => {
    const arabicDescriptionErrors =
      methods.formState.errors['description-ar'] ||
      methods.formState.errors['title-ar'] ||
      methods.formState.errors['address-ar'];
    const englishDescriptionErrors =
      methods.formState.errors['description-en'] ||
      methods.formState.errors['title-en'] ||
      methods.formState.errors['address-en'];
    if (locale === Languages.ARABIC) {
      if (arabicDescriptionErrors) setActiveTab(0);
      else if (englishDescriptionErrors) setActiveTab(1);
    }
    if (locale === Languages.ENGLISH) {
      if (englishDescriptionErrors) setActiveTab(0);
      else if (arabicDescriptionErrors) setActiveTab(1);
    }
  }, [methods.formState.errors]);

  useEffect(() => {
    if (!isFetchingListingDetails && !isListingDetailsSuccess)
      navigate(ADD_LISTING_URL.INITIALIZE);
  }, [isListingDetailsSuccess, isFetchingListingDetails]);

  useEffect(() => {
    setActiveTab(0);
  }, [pathName]);

  useEffect(() => {
    if (listingDetails) {
      const localizedPrefill = getLocalizedListingPrefill({
        translations: listingDetails.translations,
      });
      if (
        listingDetails.translations.some(
          (item) => item.title || item.address || item.description,
        )
      ) {
        methods.setValue('description-ar', localizedPrefill['description-ar']);
        methods.setValue('description-en', localizedPrefill['description-en']);
        methods.setValue('title-ar', localizedPrefill['title-ar']);
        methods.setValue('title-en', localizedPrefill['title-en']);
        methods.setValue('address-ar', localizedPrefill['address-ar']);
        methods.setValue('address-en', localizedPrefill['address-en']);
      }
    }
    if (isListingDetailsSuccess) {
      const isOwner = listingDetails?.user.id === userData?.user?.id;
      const isPublisher =
        listingDetails?.publisher_user?.id === userData?.user?.id;
      if (!isOwner && !isPublisher) {
        navigate(ADD_LISTING_URL.INITIALIZE);
      }
    }
  }, [isFetchingListingDetails, isListingDetailsSuccess]);

  useEffect(() => {
    refetch();
  }, []);

  if (isFetchingListingDetails) return <Loader />;

  return (
    <div className="flex flex-col gap-4x w-full">
      <p className="text-title_2">{t('listingDescription')}</p>
      <DescriptionHelpers className="block md:hidden" />
      <div className="flex flex-col gap-4x md:gap-5x">
        <FormProvider {...methods}>
          <Tabs
            tapsTitles={[
              {
                title:
                  locale === 'ar'
                    ? t('arabic', { lng: 'ar' })
                    : t('english', { lng: 'en' }),
              },
              {
                title:
                  locale === 'ar'
                    ? t('english', { lng: 'en' })
                    : t('arabic', { lng: 'ar' }),
              },
            ]}
            setActiveTabIndex={setActiveTab}
            activeTabIndex={activeTab}
          >
            <DescriptionForm
              language={locale === 'ar' ? 'arabic' : 'english'}
              handleGenerateValueByAI={handleGenerateValueByAI}
              keysGeneratedByAI={keysGeneratedByAI}
            />
            <DescriptionForm
              language={locale === 'ar' ? 'english' : 'arabic'}
              handleGenerateValueByAI={handleGenerateValueByAI}
              keysGeneratedByAI={keysGeneratedByAI}
            />
          </Tabs>
        </FormProvider>
        <div>
          <Button
            onClick={methods.handleSubmit(onSubmit)}
            variant="primary"
            size="medium"
            className="w-[135px]"
            isLoading={isLoadingAddListingDetails}
          >
            {t('follow')}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ListingDescription;
